import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { NormativeVersion, UpsertNormativeVersionBody } from "./types/normativeVersions";

export const useNormativeVersionsApi = () => {
  const api = useApi();

  const getNormativeVersion = useCallback(
    (normativeId: string, versionId: string, signal: AbortSignal) => {
      const url = `/admin/settings/patrol/normatives/${normativeId}/versions/${versionId}`;
      return useSWRMutation<NormativeVersion, CustomError, string>(url, (url) =>
        api.get<NormativeVersion>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminNormativeVersions = useCallback(
    (normativeId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/patrol/normatives/${normativeId}/versions`, query);
      return useSWR<Paginated<NormativeVersion>, CustomError, string>(url, (url) =>
        api.get<Paginated<NormativeVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const insertNormativeVersion = useCallback(
    async (normativeId: string, body: UpsertNormativeVersionBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/normatives/${normativeId}/versions`;
      const id = await api.post<{ id: string }, UpsertNormativeVersionBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateNormativeVersion = useCallback(
    async (normativeId: string, versionId: string, body: UpsertNormativeVersionBody): Promise<void> => {
      const url = `/admin/settings/patrol/normatives/${normativeId}/versions/${versionId}`;
      await api.put<UpsertNormativeVersionBody>(url, body);
      mutateCache([`/admin/settings/patrol/normatives/${normativeId}/versions`]);
    },
    [api],
  );

  const deleteNormativeVersion = useCallback(
    async (normativeId: string, versionId: string): Promise<void> => {
      const url = `/admin/settings/patrol/normatives/${normativeId}/versions/${versionId}`;
      await api.del(url);
      mutateCache([`/admin/settings/patrol/normatives/${normativeId}/versions`]);
    },
    [api],
  );

  const updateAdminNormativeVersionVisibility = useCallback(
    async (normativeId: string, versionId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/normatives/${normativeId}/versions/${versionId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/patrol/normatives/${normativeId}/versions`]);
    },
    [api],
  );

  return {
    getNormativeVersion,
    useAdminNormativeVersions,
    insertNormativeVersion,
    updateNormativeVersion,
    updateAdminNormativeVersionVisibility,
    deleteNormativeVersion,
  };
};
